/**
 * LandingPagesSettings - Admin component for customizing module landing pages
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { landingPagesAPI } from '@/lib/api';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MediaUpload } from '@/components/MediaUpload';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';

// Icons
import {
  Calendar, Church, Heart, BookOpen, Target, Settings, Pencil, Eye,
  Loader2, RefreshCw, Palette, Type, Link2, ExternalLink, RotateCcw,
  CheckCircle2, Image
} from 'lucide-react';

const PAGE_ICONS = {
  events: Calendar,
  churches: Church,
  giving: Heart,
  bible: BookOpen,
  campaigns: Target
};

const PAGE_LABELS = {
  events: 'Events',
  churches: 'Churches',
  giving: 'Giving/Finance',
  bible: 'Bible',
  campaigns: 'Campaigns'
};

const PAGE_ROUTES = {
  events: '/events',
  churches: '/find-church',
  giving: '/giving',
  bible: '/bible-home',
  campaigns: '/'
};

const LandingPagesSettings = () => {
  const queryClient = useQueryClient();
  const [editingPage, setEditingPage] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Fetch all landing page configs
  const { data: pages = {}, isLoading } = useQuery({
    queryKey: ['landing-pages-config'],
    queryFn: async () => {
      const res = await landingPagesAPI.getAll();
      return res.data;
    }
  });

  // Update page mutation
  const updateMutation = useMutation({
    mutationFn: async ({ pageId, data }) => {
      return landingPagesAPI.updatePage(pageId, data);
    },
    onSuccess: () => {
      toast.success('Landing page updated!');
      queryClient.invalidateQueries(['landing-pages-config']);
      setEditingPage(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  });

  // Reset page mutation
  const resetMutation = useMutation({
    mutationFn: async (pageId) => {
      return landingPagesAPI.resetPage(pageId);
    },
    onSuccess: () => {
      toast.success('Landing page reset to defaults');
      queryClient.invalidateQueries(['landing-pages-config']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to reset');
    }
  });

  const openEditDialog = (pageId) => {
    const config = pages[pageId] || {};
    setEditForm({
      title: config.title || '',
      subtitle: config.subtitle || '',
      hero_image: config.hero_image || '',
      primary_color: config.primary_color || '#3B82F6',
      secondary_color: config.secondary_color || '#2563EB',
      cta_primary_text: config.cta_primary_text || '',
      cta_primary_link: config.cta_primary_link || '',
      cta_secondary_text: config.cta_secondary_text || '',
      cta_secondary_link: config.cta_secondary_link || '',
      features_enabled: config.features_enabled !== false,
      enabled: config.enabled !== false
    });
    setEditingPage(pageId);
  };

  const handleSave = () => {
    updateMutation.mutate({
      pageId: editingPage,
      data: editForm
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Palette className="h-6 w-6 text-purple-600" />
            Landing Pages
          </h2>
          <p className="text-muted-foreground">
            Customize the hero, colors, and content for each module landing page
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => queryClient.invalidateQueries(['landing-pages-config'])}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Pages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(pages).map(([pageId, config]) => {
          const Icon = PAGE_ICONS[pageId] || Settings;
          const label = PAGE_LABELS[pageId] || pageId;
          const route = PAGE_ROUTES[pageId] || '/';
          
          return (
            <Card key={pageId} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${config.primary_color}20`, color: config.primary_color }}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{label}</CardTitle>
                      <CardDescription className="text-xs">{route}</CardDescription>
                    </div>
                  </div>
                  <Badge variant={config.enabled !== false ? "default" : "secondary"}>
                    {config.enabled !== false ? 'Active' : 'Disabled'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {config.title}
                </p>
                
                {/* Color Preview */}
                <div className="flex items-center gap-2">
                  <div 
                    className="w-6 h-6 rounded-full border"
                    style={{ backgroundColor: config.primary_color }}
                    title="Primary Color"
                  />
                  <div 
                    className="w-6 h-6 rounded-full border"
                    style={{ backgroundColor: config.secondary_color }}
                    title="Secondary Color"
                  />
                  <span className="text-xs text-muted-foreground ml-auto">
                    {config.features?.length || 0} features
                  </span>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => openEditDialog(pageId)}
                    className="flex-1"
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <a 
                    href={route} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" size="sm">
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                  </a>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      if (confirm(`Reset ${label} landing page to defaults?`)) {
                        resetMutation.mutate(pageId);
                      }
                    }}
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingPage} onOpenChange={(open) => !open && setEditingPage(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit {PAGE_LABELS[editingPage]} Landing Page
            </DialogTitle>
            <DialogDescription>
              Customize the hero section, colors, and content
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="content" className="w-full">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3">
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="colors">Colors</TabsTrigger>
              <TabsTrigger value="cta">Call to Action</TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="space-y-4 pt-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="enabled">Page Enabled</Label>
                <Switch
                  id="enabled"
                  checked={editForm.enabled}
                  onCheckedChange={(checked) => setEditForm({ ...editForm, enabled: checked })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title">Hero Title</Label>
                <Input
                  id="title"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Enter hero title"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subtitle">Hero Subtitle</Label>
                <Textarea
                  id="subtitle"
                  value={editForm.subtitle}
                  onChange={(e) => setEditForm({ ...editForm, subtitle: e.target.value })}
                  placeholder="Enter hero subtitle"
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="hero_image">Hero background image</Label>
                <MediaUpload value={editForm.hero_image} purpose="hero"
                             testId="landing-pages-hero-upload"
                             onChange={(url) => setEditForm({ ...editForm, hero_image: url })} />
                <p className="text-xs text-muted-foreground">
                  Leave empty to use gradient background
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="features_enabled">Show Features Section</Label>
                <Switch
                  id="features_enabled"
                  checked={editForm.features_enabled}
                  onCheckedChange={(checked) => setEditForm({ ...editForm, features_enabled: checked })}
                />
              </div>
            </TabsContent>

            <TabsContent value="colors" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_color">Primary Color</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={editForm.primary_color}
                      onChange={(e) => setEditForm({ ...editForm, primary_color: e.target.value })}
                      className="w-12 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={editForm.primary_color}
                      onChange={(e) => setEditForm({ ...editForm, primary_color: e.target.value })}
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="secondary_color">Secondary Color</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={editForm.secondary_color}
                      onChange={(e) => setEditForm({ ...editForm, secondary_color: e.target.value })}
                      className="w-12 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={editForm.secondary_color}
                      onChange={(e) => setEditForm({ ...editForm, secondary_color: e.target.value })}
                      placeholder="#2563EB"
                    />
                  </div>
                </div>
              </div>
              
              {/* Color Preview */}
              <div className="p-4 rounded-lg" style={{ 
                background: `linear-gradient(135deg, ${editForm.primary_color} 0%, ${editForm.secondary_color} 100%)` 
              }}>
                <p className="text-white font-semibold text-center">Color Preview</p>
              </div>
            </TabsContent>

            <TabsContent value="cta" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Primary Button Text</Label>
                  <Input
                    value={editForm.cta_primary_text}
                    onChange={(e) => setEditForm({ ...editForm, cta_primary_text: e.target.value })}
                    placeholder="Get Started"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Primary Button Link</Label>
                  <Input
                    value={editForm.cta_primary_link}
                    onChange={(e) => setEditForm({ ...editForm, cta_primary_link: e.target.value })}
                    placeholder="/login"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Secondary Button Text</Label>
                  <Input
                    value={editForm.cta_secondary_text}
                    onChange={(e) => setEditForm({ ...editForm, cta_secondary_text: e.target.value })}
                    placeholder="Learn More"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Secondary Button Link</Label>
                  <Input
                    value={editForm.cta_secondary_link}
                    onChange={(e) => setEditForm({ ...editForm, cta_secondary_link: e.target.value })}
                    placeholder="/about"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditingPage(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPagesSettings;
